// SPDX-License-Identifier: AGPL-3.0-only

import { defineStore } from "pinia";
import {
  createSmtpProfile,
  deleteSmtpProfile,
  loadEmailDeliveryRouting,
  loadRegistrationSettings,
  loadSmtpProfiles,
  loadEmailTemplates,
  loadNotificationRuntimeSettings,
  loadProbeOperationsSettings,
  loadAuditIdentityRetention,
  previewEmailTemplate,
  purgeExpiredAuditIdentities,
  saveEmailDeliveryRoute,
  saveRegistrationSettings,
  saveEmailTemplate,
  saveNotificationRuntimeSettings,
  saveProbeOperationsSettings,
  saveAuditIdentityRetention,
  sendSmtpTestEmail,
  updateSmtpProfile,
  type EmailDeliveryCategory,
  type AuditIdentityPurgeResult,
  type AuditIdentityRetentionSettings,
  type EmailDeliveryRoute,
  type EmailDeliveryRouting,
  type EmailTemplate,
  type EmailTemplateKey,
  type EmailTemplateList,
  type EmailTemplatePreview,
  type NotificationRuntimeSettings,
  type ProbeOperationsSettings,
  type RegistrationSettings,
  type SaveSmtpProfileInput,
  type SaveEmailTemplateInput,
  type SendTestEmailInput,
  type SendTestEmailResult,
  type SmtpProfile,
  type SmtpProfileList,
  type UpdateRegistrationSettingsInput,
  type UpdateNotificationRuntimeSettingsInput,
  type UpdateProbeOperationsSettingsInput,
  type UpdateAuditIdentityRetentionInput,
} from "../api/system-settings.js";

export type SystemSettingsStatus = "idle" | "loading" | "ready" | "failed";

interface SystemSettingsState {
  status: SystemSettingsStatus;
  probeOperations: ProbeOperationsSettings | null;
  notificationRuntime: NotificationRuntimeSettings | null;
  registration: RegistrationSettings | null;
  auditIdentityRetention: AuditIdentityRetentionSettings | null;
  smtpProfiles: SmtpProfileList | null;
  emailRouting: EmailDeliveryRouting | null;
  emailTemplates: EmailTemplateList | null;
}

export const useSystemSettingsStore = defineStore("system-settings", {
  state: (): SystemSettingsState => ({
    status: "idle",
    probeOperations: null,
    notificationRuntime: null,
    registration: null,
    auditIdentityRetention: null,
    smtpProfiles: null,
    emailRouting: null,
    emailTemplates: null,
  }),
  actions: {
    async load(): Promise<void> {
      if (this.status === "loading" || this.status === "ready") {
        return;
      }
      this.status = "loading";
      try {
        const results: [ProbeOperationsSettings, NotificationRuntimeSettings, RegistrationSettings, AuditIdentityRetentionSettings, SmtpProfileList, EmailDeliveryRouting, EmailTemplateList] = await Promise.all([
          loadProbeOperationsSettings(),
          loadNotificationRuntimeSettings(),
          loadRegistrationSettings(),
          loadAuditIdentityRetention(),
          loadSmtpProfiles(),
          loadEmailDeliveryRouting(),
          loadEmailTemplates(),
        ]);
        this.probeOperations = results[0];
        this.notificationRuntime = results[1];
        this.registration = results[2];
        this.auditIdentityRetention = results[3];
        this.smtpProfiles = results[4];
        this.emailRouting = results[5];
        this.emailTemplates = results[6];
        this.status = "ready";
      } catch (error: unknown) {
        this.status = "failed";
        throw error;
      }
    },
    async updateRegistration(
      input: UpdateRegistrationSettingsInput,
    ): Promise<RegistrationSettings> {
      const settings: RegistrationSettings = await saveRegistrationSettings(input);
      this.registration = settings;
      return settings;
    },
    async updateProbeOperations(
      input: UpdateProbeOperationsSettingsInput,
    ): Promise<ProbeOperationsSettings> {
      const settings: ProbeOperationsSettings = await saveProbeOperationsSettings(input);
      this.probeOperations = settings;
      return settings;
    },
    async updateNotificationRuntime(
      input: UpdateNotificationRuntimeSettingsInput,
    ): Promise<NotificationRuntimeSettings> {
      const settings: NotificationRuntimeSettings = await saveNotificationRuntimeSettings(input);
      this.notificationRuntime = settings;
      return settings;
    },
    async updateAuditIdentityRetention(
      input: UpdateAuditIdentityRetentionInput,
    ): Promise<AuditIdentityRetentionSettings> {
      const settings: AuditIdentityRetentionSettings = await saveAuditIdentityRetention(input);
      this.auditIdentityRetention = settings;
      return settings;
    },
    async purgeExpiredAuditIdentities(): Promise<AuditIdentityPurgeResult> {
      return purgeExpiredAuditIdentities();
    },
    async createSmtp(input: SaveSmtpProfileInput): Promise<SmtpProfile> {
      const profile: SmtpProfile = await createSmtpProfile(input);
      await this.reloadMailSettings();
      return profile;
    },
    async updateSmtp(profileId: string, input: SaveSmtpProfileInput): Promise<SmtpProfile> {
      const profile: SmtpProfile = await updateSmtpProfile(profileId, input);
      await this.reloadMailSettings();
      return profile;
    },
    async deleteSmtp(profileId: string): Promise<void> {
      await deleteSmtpProfile(profileId);
      await this.reloadMailSettings();
    },
    async updateEmailRoute(
      category: EmailDeliveryCategory,
      smtpProfileId: string | null,
    ): Promise<EmailDeliveryRoute> {
      const route: EmailDeliveryRoute = await saveEmailDeliveryRoute(category, smtpProfileId);
      const currentRoutes: readonly EmailDeliveryRoute[] = this.emailRouting?.routes ?? [];
      this.emailRouting = {
        routes: currentRoutes.map(
          (current: EmailDeliveryRoute): EmailDeliveryRoute =>
            current.category === category ? route : current,
        ),
      };
      return route;
    },
    async reloadMailSettings(): Promise<void> {
      const results: [SmtpProfileList, EmailDeliveryRouting] = await Promise.all([
        loadSmtpProfiles(),
        loadEmailDeliveryRouting(),
      ]);
      this.smtpProfiles = results[0];
      this.emailRouting = results[1];
    },
    async updateEmailTemplate(
      templateKey: EmailTemplateKey,
      input: SaveEmailTemplateInput,
    ): Promise<EmailTemplate> {
      const template: EmailTemplate = await saveEmailTemplate(templateKey, input);
      const currentTemplates: readonly EmailTemplate[] = this.emailTemplates?.templates ?? [];
      this.emailTemplates = {
        templates: currentTemplates.map(
          (current: EmailTemplate): EmailTemplate => current.key === template.key ? template : current,
        ),
      };
      return template;
    },
    async previewEmailTemplate(
      templateKey: EmailTemplateKey,
      input: SaveEmailTemplateInput,
    ): Promise<EmailTemplatePreview> {
      return previewEmailTemplate(templateKey, input);
    },
    async sendTestEmail(
      profileId: string,
      input: SendTestEmailInput,
    ): Promise<SendTestEmailResult> {
      return sendSmtpTestEmail(profileId, input);
    },
    reset(): void {
      this.status = "idle";
      this.probeOperations = null;
      this.notificationRuntime = null;
      this.registration = null;
      this.auditIdentityRetention = null;
      this.smtpProfiles = null;
      this.emailRouting = null;
      this.emailTemplates = null;
    },
  },
});
